import React, { useEffect, useState, useCallback } from "react";
import { Backdrop, Box, Button, Typography } from "@mui/material";
import WifiOffIcon from "@mui/icons-material/WifiOff";
import RefreshIcon from "@mui/icons-material/Refresh";
import { networkBus } from "./networkBus";

export const NetworkStatusProvider = ({ children }) => {
  const [browserOffline, setBrowserOffline] = useState(
    typeof navigator !== "undefined" ? !navigator.onLine : false
  );
  const [apiNetworkError, setApiNetworkError] = useState(false);

  const isOffline = browserOffline || apiNetworkError;

  // Browser-level connectivity events
  useEffect(() => {
    const handleOnline = () => {
      setBrowserOffline(false);
      // Don't auto-clear apiNetworkError — the user explicitly retries
    };
    const handleOffline = () => setBrowserOffline(true);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // Axios interceptor bus subscription
  useEffect(() => {
    const unsubscribe = networkBus.subscribe(() => {
      setApiNetworkError(true);
    });
    return unsubscribe;
  }, []);

  const handleRetry = useCallback(() => {
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      // Still offline — re-check on next online event; nothing to do.
      return;
    }
    // Clear and reload to re-fire in-flight queries / current screen.
    setApiNetworkError(false);
    window.location.reload();
  }, []);

  return (
    <>
      {children}
      <Backdrop
        open={isOffline}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 20,
          backdropFilter: "blur(6px)",
          backgroundColor: "rgba(15, 23, 42, 0.55)",
        }}
      >
        <Box
          role="alertdialog"
          aria-live="assertive"
          sx={{
            width: "min(92vw, 420px)",
            backgroundColor: "#fff",
            borderRadius: "22px",
            p: 4,
            textAlign: "center",
            boxShadow: "0 24px 60px rgba(0,0,0,0.28)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          {/* Icon with pulsing red ring */}
          <Box
            sx={{
              position: "relative",
              width: 96,
              height: 96,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 96,
                height: 96,
                borderRadius: "50%",
                border: "2px solid rgba(211,47,47,0.35)",
                animation: "tiqNetPulse 1.8s ease-in-out infinite",
                "@keyframes tiqNetPulse": {
                  "0%, 100%": {
                    transform: "scale(1)",
                    opacity: 1,
                  },
                  "50%": {
                    transform: "scale(1.12)",
                    opacity: 0.4,
                  },
                },
              }}
            />
            <Box
              sx={{
                width: 70,
                height: 70,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ef5350 0%, #b71c1c 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 8px 20px rgba(183,28,28,0.35)",
              }}
            >
              <WifiOffIcon sx={{ color: "#fff", fontSize: 36 }} />
            </Box>
          </Box>

          <Typography
            sx={{ fontSize: "20px", fontWeight: 700, color: "#0f172a" }}
          >
            You are offline
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#64748b",
              lineHeight: 1.6,
              px: 1,
            }}
          >
            We can't reach the server right now. Please check your internet
            connection and tap Retry once you're back online.
          </Typography>

          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={handleRetry}
            sx={{
              mt: 1,
              px: 3,
              py: 1,
              borderRadius: "10px",
              backgroundColor: "#1c97e3",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "14px",
              boxShadow: "0 6px 14px rgba(28,151,227,0.35)",
              "&:hover": { backgroundColor: "#1565c0" },
            }}
          >
            Retry
          </Button>

          {browserOffline && (
            <Typography
              sx={{
                fontSize: "12px",
                color: "#b91c1c",
                fontWeight: 600,
                mt: 0.5,
              }}
            >
              You are currently offline.
            </Typography>
          )}
        </Box>
      </Backdrop>
    </>
  );
};
